import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowPathIcon, BuildingOffice2Icon, CheckCircleIcon, UserGroupIcon,
} from '@heroicons/react/24/outline';
import { fetchMyBuyerOrganisation, saveBuyerOrganisation, fetchBuyerMembers } from '../api/marketplaceApi';

const BuyerOrganisation: React.FC = () => {
  const [organisation, setOrganisation] = useState<any>(null);
  const [members, setMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState({
    name: '',
    organisation_type: 'business',
    registration_number: '',
    tax_id: '',
    contact_email: '',
    contact_phone: '',
    contact_person: '',
    address: '',
    city: '',
    country: 'Rwanda',
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [orgRes, membersRes] = await Promise.allSettled([
        fetchMyBuyerOrganisation(),
        fetchBuyerMembers(),
      ]);
      if (orgRes.status === 'fulfilled' && orgRes.value.organisation) {
        const org = orgRes.value.organisation;
        setOrganisation(org);
        setForm({
          name: org.name || '',
          organisation_type: org.organisation_type || 'business',
          registration_number: org.registration_number || '',
          tax_id: org.tax_id || '',
          contact_email: org.contact_email || '',
          contact_phone: org.contact_phone || '',
          contact_person: org.contact_person || '',
          address: org.address || '',
          city: org.city || '',
          country: org.country || 'Rwanda',
        });
      }
      if (membersRes.status === 'fulfilled') {
        setMembers(membersRes.value.members || []);
      }
    } catch (err) {
      console.error('Failed to load organisation:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      alert('Organisation name is required');
      return;
    }
    setSaving(true);
    setSaved(false);
    try {
      await saveBuyerOrganisation(form);
      setSaved(true);
      loadData();
      setTimeout(() => setSaved(false), 3000);
    } catch (err: any) {
      alert(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <ArrowPathIcon className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Buyer Organisation</h2>
          <p className="text-sm text-gray-500 mt-0.5">Manage your organisation details and team members</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50"
        >
          {saving ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <CheckCircleIcon className="w-4 h-4" />}
          {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Changes'}
        </button>
      </div>

      {/* Organisation Form */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
            <BuildingOffice2Icon className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Organisation Details</h3>
            <p className="text-xs text-gray-500">Your buyer organisation information</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Organisation Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="e.g., Green Farms Ltd"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Organisation Type</label>
            <select
              value={form.organisation_type}
              onChange={e => setForm({ ...form, organisation_type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
            >
              <option value="business">Business</option>
              <option value="cooperative">Cooperative</option>
              <option value="ngo">NGO</option>
              <option value="government">Government</option>
              <option value="individual">Individual</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Registration Number</label>
            <input
              type="text"
              value={form.registration_number}
              onChange={e => setForm({ ...form, registration_number: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tax ID</label>
            <input
              type="text"
              value={form.tax_id}
              onChange={e => setForm({ ...form, tax_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
            <input
              type="text"
              value={form.contact_person}
              onChange={e => setForm({ ...form, contact_person: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
            <input
              type="email"
              value={form.contact_email}
              onChange={e => setForm({ ...form, contact_email: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input
              type="tel"
              value={form.contact_phone}
              onChange={e => setForm({ ...form, contact_phone: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
            <input
              type="text"
              value={form.city}
              onChange={e => setForm({ ...form, city: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
            <input
              type="text"
              value={form.address}
              onChange={e => setForm({ ...form, address: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
            <input
              type="text"
              value={form.country}
              onChange={e => setForm({ ...form, country: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
            />
          </div>
        </div>
      </div>

      {/* Team Members */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <UserGroupIcon className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Team Members</h3>
            <p className="text-xs text-gray-500">{members.length} member{members.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
        {members.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">No team members yet</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {members.map((member: any) => (
              <div key={member.id} className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-emerald-100 rounded-full flex items-center justify-center">
                    <span className="text-xs font-bold text-emerald-700">
                      {(member.full_name || 'U').charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{member.full_name || 'Unknown'}</p>
                    <p className="text-xs text-gray-500">{member.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    member.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {member.status || 'Active'}
                  </span>
                  <span className="text-xs text-gray-400 capitalize">{member.role || 'Member'}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BuyerOrganisation;
